import React from 'react';
import {
  PageHeader,
  Timeline,
  ShimmerLoader,
  PageState,
  PageErrorState,
  Reveal,
} from '@/shared/ui';
import { ExperienceCard } from './components/experience-card';
import { useExperience, useSiteContent } from '@personal-portfolio/queries';
import { useLocation } from 'react-router';
import { Helmet } from '@dr.pogodin/react-helmet';
import { toErrorMessage } from '@/shared/lib/query-error';

export const ExperiencePage: React.FC = () => {
  const {
    data: experienceData,
    isPending: experienceLoading,
    isFetching: experienceFetching,
    isPaused: experiencePaused,
    error: experienceError,
    refetch: refetchExperience,
  } = useExperience();
  const error = toErrorMessage(experienceError, experiencePaused);
  const location = useLocation();
  const content = useSiteContent();
  const page = content.pages.experience;

  const handleRetry = () => {
    if (experienceFetching) return;
    void refetchExperience();
  };

  const renderLoading = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <div className="space-y-8">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="rounded-xl border border-border p-6">
            <ShimmerLoader lines={3} height="h-6" className="mb-4" />
            <ShimmerLoader lines={2} height="h-4" className="mb-3" />
            <ShimmerLoader lines={1} height="h-4" className="w-1/2" />
          </div>
        ))}
      </div>
    </>
  );

  const renderError = () => (
    <>
      <PageHeader title={page.title} subtitle={page.subtitle} />
      <PageErrorState onRetry={handleRetry} message={error ?? undefined} />
    </>
  );

  const renderContent = () => {
    if (!experienceData?.length) {
      return (
        <>
          <PageHeader title={page.title} subtitle={page.subtitle} />
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-6xl text-muted-foreground">
                <span role="img" aria-label="Briefcase">
                  💼
                </span>
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                {page.empty.title}
              </h3>
              <p className="text-muted-foreground">{page.empty.message}</p>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <PageHeader title={page.title} subtitle={page.subtitle} />
        <Timeline>
          {experienceData.map((exp) => (
            <Reveal key={exp.slug}>
              <ExperienceCard experience={exp} />
            </Reveal>
          ))}
        </Timeline>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>{page.metaTitle}</title>
        <link
          rel="canonical"
          href={`${content.site.url}${location.pathname}`}
        />
      </Helmet>
      <PageState
        loading={experienceLoading}
        error={error}
        hasData={!!experienceData}
        renderLoading={renderLoading}
        renderError={renderError}
        renderContent={renderContent}
      />
    </>
  );
};
